import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FirebaseError } from "firebase/app";
import { MarketingNav } from "../../chrome/MarketingNav";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/lib/auth-context";
import { authErrorMessage } from "@/lib/auth-errors";

// Long enough to stop impatient double-sends, short enough not to strand
// someone who genuinely needs a second mail.
//
// This is a UX guard, not a rate limit. It cannot stop abuse: the Firebase web
// API key is public, so a script calls Identity Toolkit's sendOobCode directly
// and never loads this page. Real throttling has to live server-side — see the
// design spec for why that's a separate, cross-repo piece of work.
const RESEND_COOLDOWN_SECONDS = 60;

// The login page hands over the address the user already typed, so the common
// case (wrong password on an email you just entered) doesn't make them type it
// twice. The route is also reachable directly, so treat the state as untrusted.
function emailFromState(state: unknown): string {
  if (state && typeof state === "object" && "email" in state) {
    const value = (state as { email: unknown }).email;
    if (typeof value === "string") return value;
  }
  return "";
}

export default function ParolaUitataPage() {
  const { sendPasswordReset } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState(() => emailFromState(location.state));
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // One interval for the whole countdown, keyed on whether it's running — not a
  // fresh timer per second, which would re-subscribe on every tick.
  const cooling = cooldown > 0;
  useEffect(() => {
    if (!cooling) return;
    const id = setInterval(() => setCooldown((s) => (s <= 1 ? 0 : s - 1)), 1000);
    return () => clearInterval(id);
  }, [cooling]);

  const markSent = () => {
    setSent(true);
    setCooldown(RESEND_COOLDOWN_SECONDS);
  };

  const send = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await sendPasswordReset(email);
      markSent();
    } catch (err) {
      // An unknown address is deliberately indistinguishable from success —
      // this form must not answer "does this email have an account here?".
      // Firebase's email-enumeration protection already resolves rather than
      // throwing; this keeps the behaviour identical if it's ever switched off.
      if (err instanceof FirebaseError && err.code === "auth/user-not-found") {
        markSent();
        return;
      }
      setError(authErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void send();
  };

  return (
    <>
      <Helmet>
        <title>Resetează parola — Asistent Auto</title>
      </Helmet>
      <MarketingNav />
      <div className="auth-wrap">
        <div className="auth-card">
          {sent ? (
            <div className="auth-sent">
              <Icon name="mark_email_read" className="auth-sent__icon" />
              <h1>Verifică-ți e-mailul</h1>
              <p>
                Dacă există un cont pentru <strong>{email}</strong>, am trimis un
                link de resetare a parolei.
              </p>
              <p className="auth-sent__hint">
                Nu l-ai primit? Verifică folderul spam sau{" "}
                <button
                  type="button"
                  className="auth-linkbtn"
                  onClick={() => void send()}
                  disabled={submitting || cooling}
                >
                  {cooling
                    ? `trimite din nou peste ${cooldown} s`
                    : "trimite din nou"}
                </button>
                .
              </p>
            </div>
          ) : (
            <>
              <h1>Resetează parola</h1>
              <p className="lede">
                Îți trimitem un link de resetare pe email. Folosește adresa
                contului tău.
              </p>
              <form className="auth-form" onSubmit={onSubmit}>
                {error && <div className="auth-error">{error}</div>}
                <div className="auth-field">
                  <label htmlFor="email">Email</label>
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn--primary btn--lg"
                  disabled={submitting}
                >
                  {submitting ? "Trimitem…" : "Trimite linkul"}
                </button>
              </form>
            </>
          )}
          <div className="auth-foot">
            <Link to="/cont/login">← Înapoi la autentificare</Link>
          </div>
        </div>
      </div>
    </>
  );
}
